import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useAuth } from './hooks/useAuth';
import Activate from './pages/Activate';
import PageMeta from './components/PageMeta';
import TopNavLayout from './components/TopNavLayout';
import ViewerLayout from './components/ViewerLayout';
import Dashboard from './pages/Dashboard';
import Viewer from './pages/Viewer';
import Merge from './pages/Merge';
import SplitExtract from './pages/SplitExtract';
import Compress from './pages/Compress';
import Sign from './pages/Sign';
import Protect from './pages/Protect';
import Convert from './pages/Convert';
import Invoice from './pages/Invoice';
import Batch from './pages/Batch';
import Watermark from './pages/Watermark';
import PageNumbers from './pages/PageNumbers';
import Flatten from './pages/Flatten';
import Crop from './pages/Crop';
import Redact from './pages/Redact';
import OCR from './pages/OCR';
import DeletePages from './pages/DeletePages';
import Rotate from './pages/Rotate';
import Compare from './pages/Compare';
import Setup from './pages/Setup';
import Welcome from './pages/Welcome';
import Onboarding from './pages/Onboarding';
import Help from './pages/Help';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Account from './pages/Account';
import DesktopProfileEdit from './components/desktop/DesktopProfileEdit';
import DesktopDevices from './components/desktop/DesktopDevices';
import Notifications from './pages/Notifications';
import Support from './pages/Support';
import SupportThread from './pages/SupportThread';
import Billing from './pages/Billing';
import Cloud from './pages/Cloud';
import EsignDashboard from './pages/EsignDashboard';
import EsignCompose from './pages/EsignCompose';
import EsignDetail from './pages/EsignDetail';
import EsignSettings from './pages/EsignSettings';
import EsignTemplates from './pages/EsignTemplates';
import RecentFilesView from './components/desktop/RecentFilesView';
import CategoryLauncher from './components/desktop/CategoryLauncher';
import SignDocument from './pages/SignDocument';
import Verify from './pages/Verify';
import { ProGate } from './components/ProBadge';
import TrialBanner from './components/TrialBanner';
import ActivationModal from './components/ActivationModal';
import UpdateNotification from './components/UpdateNotification';
import SessionExpiredModal from './components/SessionExpiredModal';
import ConnectivityBanner from './components/ConnectivityBanner';
import LanguageDetectedBanner from './components/LanguageDetectedBanner';
import CommandPalette from './components/CommandPalette';
import OfflineEnginesPromptHost from './components/OfflineEnginesPromptHost';
import { isDesktop } from './lib/fileSystem';
import { isDesktop as isDesktopPlatform } from './lib/platform';
import { checkLicenseOnLaunch } from './lib/licenseCheck';
import { isPublicAuthPath, hasCachedSession, shouldRedirectToLogin } from './lib/authGate';
import { verifyStoredLicense, hasValidLicense } from './lib/licenseToken';
import { auth } from './lib/firebase';
import { openExternalUrl, SCRIVAR_BILLING_URL } from './lib/external';
import { useSessionHeartbeat } from './hooks/useSessionHeartbeat';
import { reclaimSession, signOut } from './lib/auth';
import { FileDialogProvider } from './contexts/FileDialogContext';
import { ContactPanelProvider } from './contexts/ContactPanelContext';

// Global ref for Viewer's save handler — set by Viewer when mounted
export const viewerSaveRef = { current: null, saveAs: null, print: null, find: null };

/**
 * PDF Suite — Main App Shell
 *
 * Routes:
 *   /            → Redirects to /tools
 *   /tools       → Dashboard (tool grid + file upload)
 *   /viewer      → PDF Viewer
 *   /merge       → Merge PDFs
 *   /split       → Split & Extract (tabbed, ?tab=extract or ?tab=split)
 *   /compress    → Compress PDF
 *   /convert     → Convert files
 *   /sign        → Sign PDF
 *   /protect     → Protect / Unlock PDF
 *   /invoice     → Invoice Builder
 *   /batch       → Batch Processing
 *   /watermark   → Add Watermarks
 *   /page-numbers → Add Page Numbers
 *   /flatten     → Flatten PDF
 *   /crop        → Crop PDF
 *   /redact      → Redact PDF
 *   /ocr         → OCR Text Recognition
 *   /delete      → Delete Pages
 *   /rotate      → Rotate Pages
 *   /activate    → Activate (standalone, no sidebar)
 */
export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const [showActivation, setShowActivation] = useState(false);
  const [licenseError, setLicenseError] = useState(null);
  const [setupComplete, setSetupComplete] = useState(!isDesktop());
  const [checkingSetup, setCheckingSetup] = useState(isDesktop());
  const [sessionExpired, setSessionExpired] = useState(false);
  const [, forceUpdate] = useState(0);
  // Offline-license verification gates rendering on desktop: hold the redirect
  // decision until the main-process Ed25519 check resolves (fast — IPC + one
  // signature verify). Web never waits (licenseReady starts true).
  const [licenseReady, setLicenseReady] = useState(!isDesktop());

  const { consecutiveFailures } = useSessionHeartbeat((reason) => {
    if (reason === 'session_expired') setSessionExpired(true);
  });

  // License check on launch
  useEffect(() => {
    checkLicenseOnLaunch().then((result) => {
      if (result?.locked) {
        setLicenseError(result.reason);
      }
      forceUpdate((n) => n + 1); // re-render with correct tier
    });
  }, []);

  // Verify the stored offline license before the hard gate evaluates.
  useEffect(() => {
    if (!isDesktop()) return;
    verifyStoredLicense().finally(() => setLicenseReady(true));
  }, []);

  // Check setup state on desktop
  useEffect(() => {
    if (!isDesktop()) return;
    window.electronAPI.getSetupState().then((state) => {
      setSetupComplete(state.hasCompletedSetup);
      setCheckingSetup(false);
    });
  }, []);

  // Listen for scrivar:open-activation (dispatched by TrialStatusBanner + any
  // other component that wants to open the activation modal without prop-drilling).
  useEffect(() => {
    const handler = () => setShowActivation(true);
    document.addEventListener('scrivar:open-activation', handler);
    return () => document.removeEventListener('scrivar:open-activation', handler);
  }, []);

  // Listen for Electron menu navigation, file-open, and license events
  useEffect(() => {
    if (!isDesktop()) return;

    const cleanups = [];

    cleanups.push(window.electronAPI.onNavigate((route) => {
      navigate(route);
    }));

    // File > Save / File > Save As — delegates to Viewer's save handler
    cleanups.push(window.electronAPI.onMenuSave(() => {
      if (viewerSaveRef.current) viewerSaveRef.current();
    }));
    cleanups.push(window.electronAPI.onMenuSaveAs(() => {
      if (viewerSaveRef.saveAs) viewerSaveRef.saveAs();
    }));
    if (window.electronAPI.onMenuPrint) {
      cleanups.push(window.electronAPI.onMenuPrint(() => {
        if (viewerSaveRef.print) viewerSaveRef.print();
      }));
    }
    if (window.electronAPI.onMenuFind) {
      cleanups.push(window.electronAPI.onMenuFind(() => {
        viewerSaveRef.find?.();
      }));
    }

    const openFromMain = async (filePath) => {
      try {
        const buffer = await window.electronAPI.readFile(filePath);
        const name = filePath.split(/[/\\]/).pop();
        const file = new File([buffer], name, { type: 'application/pdf' });
        navigate('/viewer', { state: { newDocument: true, file, filePath } });
      } catch (err) {
        console.error('Failed to open file from main process:', err);
      }
    };
    cleanups.push(window.electronAPI.onOpenFile(openFromMain));
    // Finder "Open With" / `open -a` / a file-association launch can arrive
    // before this listener exists (cold launch, or macOS reopening a window).
    // Main holds it until we're subscribed; pull it now so it opens straight
    // into the editor instead of only landing in Recent files.
    window.electronAPI.takePendingFile?.().then((filePath) => {
      if (filePath) openFromMain(filePath);
    }).catch(() => {});

    // Help > Enter Registration Code — navigate to account or login
    if (window.electronAPI.onShowAccountOrLogin) {
      cleanups.push(window.electronAPI.onShowAccountOrLogin(() => {
        navigate(userRef.current ? '/account' : '/login');
      }));
    }

    return () => cleanups.forEach((fn) => fn?.());
  }, [navigate]);

  if (checkingSetup) {
    return <div className="min-h-screen bg-gray-50" />;
  }

  if (!setupComplete && isDesktop()) {
    return <Setup onComplete={() => setSetupComplete(true)} />;
  }

  // Hard gate (desktop only): no anonymous use. Until the user signs in, only
  // the auth / onboarding / public-signer routes are reachable. Web keeps its
  // anonymous free-tier funnel (isDesktop() is false there).
  //
  // "Authenticated" = a live React `user`, OR Firebase's synchronous
  // `auth.currentUser` (set the instant signIn() resolves, before React catches
  // up — avoids bouncing a just-signed-in user), OR a cached session (the
  // OFFLINE fix: on an offline cold start Firebase can't restore the session,
  // so a signed-in lifetime/paid user would otherwise be bounced to /login),
  // OR a locally-verified offline license token (LOGOUT-CAPABLE activation:
  // a signed-out licensed device keeps the full local toolset; the gate waits
  // for that verification via licenseReady).
  if (shouldRedirectToLogin({
    desktop: isDesktop(),
    loading: loading || !licenseReady,
    hasUser: Boolean(user),
    hasFirebaseUser: Boolean(auth.currentUser),
    hasSession: hasCachedSession(),
    hasValidLicense: hasValidLicense(),
    isPublic: isPublicAuthPath(location.pathname),
  })) {
    return <Navigate to="/login" replace />;
  }

  return (
    <FileDialogProvider>
    <ContactPanelProvider>
    <div className="min-h-screen bg-gray-50">
      <PageMeta />
      {/* Connectivity banner — signed-in users, or any desktop install (so a
          licensed-but-signed-out desktop user still gets the offline-trust
          copy; heartbeat isn't running signed-out so failures stay 0 and
          only real online/offline events show the banner). */}
      {(user || isDesktopPlatform()) && <ConnectivityBanner consecutiveFailures={consecutiveFailures} />}
      <LanguageDetectedBanner />
      {/* TrialBanner: web-only. Desktop uses TrialStatusBanner inside DesktopShell. */}
      {!isDesktop() && (
        <TrialBanner
          onEnterCode={() => setShowActivation(true)}
          onUpgrade={() => openExternalUrl(SCRIVAR_BILLING_URL)}
        />
      )}

      <ActivationModal
        open={showActivation}
        onClose={() => setShowActivation(false)}
        onActivated={() => forceUpdate((n) => n + 1)}
      />

      {/* Contextual "download offline engines" prompt (desktop; inert on web) */}
      <OfflineEnginesPromptHost />

      <Routes>
        <Route path="/" element={<Navigate to="/tools" replace />} />
        <Route element={<TopNavLayout />}>
          <Route path="/tools" element={<Dashboard />} />
          <Route path="/merge" element={<Merge />} />
          <Route path="/split" element={<SplitExtract />} />
          <Route path="/compress" element={<Compress />} />
          <Route path="/sign" element={<Sign />} />
          <Route path="/protect" element={<Protect />} />
          <Route path="/convert" element={<Convert />} />
          <Route path="/invoice" element={<Invoice />} />
          <Route path="/batch" element={<Batch />} />
          <Route path="/watermark" element={<Watermark />} />
          <Route path="/page-numbers" element={<PageNumbers />} />
          <Route path="/flatten" element={<Flatten />} />
          <Route path="/crop" element={<Crop />} />
          <Route path="/redact" element={<Redact />} />
          <Route path="/ocr" element={<OCR />} />
          <Route path="/delete" element={<DeletePages />} />
          <Route path="/rotate" element={<Rotate />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/account" element={<Account />} />
          <Route path="/account/profile" element={<DesktopProfileEdit />} />
          <Route path="/account/devices" element={<DesktopDevices />} />
          <Route path="/account/notifications" element={<Notifications />} />
          <Route path="/account/support" element={<Support />} />
          <Route path="/account/support/:id" element={<SupportThread />} />
          <Route path="/account/billing" element={<Billing />} />
          <Route path="/cloud" element={<Cloud />} />
          <Route path="/esign" element={<ProGate feature="esign"><EsignDashboard /></ProGate>} />
          <Route path="/esign/new" element={<ProGate feature="esign"><EsignCompose /></ProGate>} />
          <Route path="/esign/templates" element={<ProGate feature="esign"><EsignTemplates /></ProGate>} />
          <Route path="/esign/:id" element={<ProGate feature="esign"><EsignDetail /></ProGate>} />
          <Route path="/esign/:id/sent" element={<Navigate to="../" replace />} />
          <Route path="/account/esign-settings" element={<ProGate feature="esign"><EsignSettings /></ProGate>} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/recent" element={<RecentFilesView />} />
          <Route path="/launch/:cat" element={<CategoryLauncher />} />
        </Route>
        <Route element={<ViewerLayout />}>
          <Route path="/viewer" element={<Viewer />} />
        </Route>
        <Route path="/activate" element={<Activate />} />
        <Route path="/sign/:token" element={<SignDocument />} />
        <Route path="/verify/:envelopeId" element={<Verify />} />
        <Route path="/login" element={<Login />} />
        {/* /register is an alias to /login with the signup tab preselected. */}
        <Route path="/register" element={<Login />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/onboarding" element={<Onboarding />} />
        {/* Catch-all — real 404 page. The /app cache-healing is handled at the
            _redirects layer (rewrites to /home before React Router runs), so this
            only catches genuine typos and broken inbound links. */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <CommandPalette />

      <UpdateNotification />

      <SessionExpiredModal
        open={sessionExpired}
        onReclaim={async () => {
          await reclaimSession();
          setSessionExpired(false);
          forceUpdate((n) => n + 1);
        }}
        onSignOut={async () => {
          setSessionExpired(false);
          await signOut();
        }}
      />
    </div>
    </ContactPanelProvider>
    </FileDialogProvider>
  );
}
